import React, { useState, useEffect } from 'react';
import './FSSCSubPages.css';
import { API_BASE, Event, RsvpModel, handleSubmit, getExistingRSVP } from '../../src/controller';
import '@fontsource/plus-jakarta-sans';

const formatEventDateTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString; // Return original string if date is invalid
    }
    
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

const formatPhoneNumber = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');
  
  // Format the number as (XXX)XXX-XXXX
  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 6) {
    return `(${numbers.slice(0, 3)})${numbers.slice(3)}`;
  } else {
    return `(${numbers.slice(0, 3)})${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  }
};

const Events: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loadingEvents, setLoadingEvents] = useState<boolean>(true);
  const [expandedEvent, setExpandedEvent] = useState<Event | null>(null);
  const [rsvps, setRsvps] = useState<RsvpModel[]>([]); // RSVPs for the currently expanded event
  const [allRsvps, setAllRsvps] = useState<RsvpModel[]>([]); // All RSVPs fetched on load
  const [loadingRsvps, setLoadingRsvps] = useState<boolean>(false);
  const [showRsvpForm, setShowRsvpForm] = useState<boolean>(true); // Default to showing RSVP form initially
  const [isEventFull, setIsEventFull] = useState<boolean>(false);

  // RSVP Form states
  const [name, setName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [guests, setGuests] = useState<number | undefined>(undefined);
  const [attending, setAttending] = useState<'Yes' | 'No' | 'Maybe'>('Yes');
  const [submittingRsvp, setSubmittingRsvp] = useState<boolean>(false);
  const [rsvpSuccess, setRsvpSuccess] = useState<string | null>(null);
  const [rsvpError, setRsvpError] = useState<string | null>(null);

  // Calculate total confirmed guests for an event
  const calculateTotalGuests = (eventId: number): number => {
    return allRsvps
      .filter(rsvp => rsvp.event_id === eventId && rsvp.attending === 'Yes')
      .reduce((total, rsvp) => total + rsvp.number_of_guests, 0);
  };

  // Update isEventFull whenever expandedEvent or allRsvps changes
  useEffect(() => {
    if (expandedEvent) {
      const totalGuests = calculateTotalGuests(expandedEvent.event_id);
      const isFull = totalGuests >= expandedEvent.max;
      setIsEventFull(isFull);
      if (isFull) {
        setShowRsvpForm(false); // Force guest list view when event is full
      }
      else{
        setShowRsvpForm(true)
      }
    }
  }, [expandedEvent, allRsvps]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch events
        const eventsResponse = await fetch(`${API_BASE}?action=events`);
        if (!eventsResponse.ok) {
          throw new Error('Failed to fetch events');
        }
        const eventsData = await eventsResponse.json();
        
        const parsedEvents: Event[] = eventsData.map((item: any) => ({
          event_id: item.event_id,
          event_name: item.event_name || '',
          event_date: item.event_date || '',
          event_snapshot: item.event_snapshot || '',
          location: item.location || '',
          details: item.details || '',
          max: item.max || 1000,
          is_hidden: item.is_hidden || false,
        }));

        setEvents(parsedEvents.filter((event: Event) => !event.is_hidden)); // Filter hidden events for display

        // Fetch all RSVPs
        const allRsvpsData = await getExistingRSVP(''); // getExistingRSVP now fetches all RSVPs
        setAllRsvps(allRsvpsData);

        // Check for event in URL query parameter on initial load
        const params = new URLSearchParams(window.location.search);
        const eventParam = params.get('event');
        if (eventParam) {
          const eventIdNum = parseInt(eventParam, 10); // Parse as number
          const eventToExpand = parsedEvents.find(event => event.event_id === eventIdNum); // Search in all events, including hidden ones
          if (eventToExpand) {
            setExpandedEvent(eventToExpand);
            setShowRsvpForm(true); // Always show RSVP form first when expanding from URL
            // Filter RSVPs for this specific event from allRsvpsData
            const filteredRsvps = allRsvpsData.filter(rsvp => rsvp.event_id === eventToExpand.event_id);
            setRsvps(filteredRsvps);
          }
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoadingEvents(false);
      }
    };

    fetchData();
  }, []); // Only run once on component mount

  const fetchRsvpsForEvent = (eventId: number) => {
    // Filter RSVPs from the already fetched allRsvps state
    setLoadingRsvps(true);
    const filteredRsvps = allRsvps.filter(rsvp => rsvp.event_id === eventId);
    setRsvps(filteredRsvps);
    setLoadingRsvps(false);
  }

  const handleEventClick = (event: Event) => {
    const params = new URLSearchParams(window.location.search);
    if (expandedEvent && expandedEvent.event_id === event.event_id) {
      // If the clicked event is already expanded, collapse it and remove the query parameter
      params.delete('event');
      window.history.pushState({}, '', `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`);
      setExpandedEvent(null);
      setRsvps([]);
    } else {
      // Otherwise, expand this event and add the query parameter
      params.set('event', event.event_id.toString()); // Convert to string for URL
      window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
      setExpandedEvent(event);
      // Check if event is full before showing RSVP form
      const totalGuests = calculateTotalGuests(event.event_id);
      setShowRsvpForm(totalGuests < event.max); // Only show RSVP form if event is not full
      // Filter RSVPs for the newly expanded event from allRsvps
      const filteredRsvps = allRsvps.filter(rsvp => rsvp.event_id === event.event_id);
      setRsvps(filteredRsvps);
    }
  };

  const handleRsvpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!expandedEvent) return;

    setSubmittingRsvp(true);
    setRsvpSuccess(null);
    setRsvpError(null);

    const rsvpData: RsvpModel = {
      name,
      phone_number: parseInt(phone.replace(/\D/g, '')), // Remove non-numeric characters before converting
      number_of_guests: guests || 0,
      attending,
      event_id: expandedEvent.event_id,
    };

    try {
      await handleSubmit(rsvpData);
      setRsvpSuccess('RSVP submitted successfully!');
      // After submission, re-fetch all RSVPs and then filter for the current event
      const updatedAllRsvps = await getExistingRSVP('');
      setAllRsvps(updatedAllRsvps); // Update the global allRsvps state
      fetchRsvpsForEvent(expandedEvent.event_id); // Refresh the currently displayed RSVPs
      // Clear form fields
      setName('');
      setPhone('');
      setGuests(undefined);
      setAttending('Yes');
    } catch (error) {
      console.error('RSVP submission error:', error);
      setRsvpError('Failed to submit RSVP. Please try again.');
    } finally {
      setSubmittingRsvp(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-8 font-sans flex flex-col items-center justify-center">
      <h2 className="text-4xl md:text-5xl font-bold mb-8 uppercase tracking-widest">Events</h2>
      
      {loadingEvents ? (
        <p className="text-center text-lg">Loading events...</p>
      ) : expandedEvent ? (
        // Display expanded event details
        <div className="w-full max-w-2xl px-2 sm:px-0 mt-8">
          <button 
            onClick={() => handleEventClick(expandedEvent)} // Pass expandedEvent to collapse it
            className="mb-4 bg-gray-700 text-white font-bold py-2 px-4 rounded-full hover:bg-gray-600 transition-colors"
          >
            &larr; Back to All Events
          </button>
          <div className="bg-black border border-white rounded-lg p-4 sm:p-6 mb-4 cursor-pointer text-center transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_15px_rgba(255,255,255,0.3)]">
            <h3 className="text-2xl sm:text-3xl font-bold mb-2 uppercase tracking-widest">{expandedEvent.event_name}</h3>
            <p className="text-md sm:text-lg mb-1">{formatEventDateTime(expandedEvent.event_date)}, <span className="font-bold">{expandedEvent.location}</span></p>
            <p className="text-md sm:text-lg mb-4">{expandedEvent.details}</p>

            <div className="flex flex-col sm:flex-row justify-center mb-6 space-y-2 sm:space-y-0 sm:space-x-4">
                {!isEventFull && (
                    <button
                        onClick={(e) => { e.stopPropagation(); setShowRsvpForm(true); }}
                        className={`w-full sm:w-auto px-4 py-2 sm:px-6 sm:py-3 rounded-full font-bold ${showRsvpForm ? 'bg-purple-400 text-white' : 'bg-gray-700 text-gray-300'}`}
                    >
                        RSVP
                    </button>
                )}
                <button
                    onClick={(e) => { e.stopPropagation(); setShowRsvpForm(false); }}
                    className={`w-full sm:w-auto px-4 py-2 sm:px-6 sm:py-3 rounded-full font-bold ${!showRsvpForm ? 'bg-purple-400 text-white' : 'bg-gray-700 text-gray-300'}`}
                >
                    Guest List
                </button>
            </div>

            {isEventFull && (
                <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 mb-6 text-center">
                    <p className="text-white font-bold">This event has reached its maximum capacity.</p>
                </div>
            )}

            {!isEventFull && showRsvpForm ? (
                <form onSubmit={handleRsvpSubmit} onClick={(e) => e.stopPropagation()} className="flex flex-col space-y-4 bg-purple-300/40 p-4 sm:p-8 rounded-lg font-['Plus_Jakarta_Sans']">
                    <input 
                        type="text" 
                        placeholder="Name" 
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="p-3 rounded-md bg-white/70 text-black placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 w-full font-['Plus_Jakarta_Sans']"
                        required
                    />
                    <input 
                        type="tel" 
                        placeholder="Phone Number" 
                        value={formatPhoneNumber(phone)}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                        className="p-3 rounded-md bg-white/70 text-black placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 w-full font-['Plus_Jakarta_Sans']"
                        required
                    />
                    <input 
                        type="number" 
                        placeholder="Number of Guests" 
                        value={guests || ''}
                        onChange={(e) => setGuests(e.target.value ? parseInt(e.target.value) : undefined)}
                        className="p-3 rounded-md bg-white/70 text-black placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 w-full font-['Plus_Jakarta_Sans']"
                        min="1"
                        required
                    />
                    <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-4 font-['Plus_Jakarta_Sans']">
                        <label className="inline-flex items-center">
                            <input 
                                type="radio" 
                                value="Yes" 
                                checked={attending === 'Yes'}
                                onChange={() => setAttending('Yes')}
                                className="form-radio h-5 w-5 text-purple-600"
                            />
                            <span className="ml-2 text-white">I'm there!</span>
                        </label>
                        <label className="inline-flex items-center">
                            <input 
                                type="radio" 
                                value="No" 
                                checked={attending === 'No'}
                                onChange={() => setAttending('No')}
                                className="form-radio h-5 w-5 text-purple-600"
                            />
                            <span className="ml-2 text-white">Can't make it</span>
                        </label>
                        <label className="inline-flex items-center">
                            <input 
                                type="radio" 
                                value="Maybe" 
                                checked={attending === 'Maybe'}
                                onChange={() => setAttending('Maybe')}
                                className="form-radio h-5 w-5 text-purple-600"
                            />
                            <span className="ml-2 text-white">Maybe</span>
                        </label>
                    </div>

                    <button 
                        type="submit" 
                        className="bg-white text-purple-800 font-bold py-3 rounded-full hover:bg-gray-200 transition-colors w-full font-['Plus_Jakarta_Sans']"
                        disabled={submittingRsvp}
                    >
                        {submittingRsvp ? 'Submitting...' : 'SUBMIT'}
                    </button>
                    {rsvpSuccess && <p className="text-green-500 text-center mt-2 font-['Plus_Jakarta_Sans']">{rsvpSuccess}</p>}
                    {rsvpError && <p className="text-red-500 text-center mt-2 font-['Plus_Jakarta_Sans']">{rsvpError}</p>}
                </form>
            ) : (
                <div className="mt-6 bg-white rounded-lg p-4 sm:p-8 shadow-lg text-black">
                    <h4 className="text-xl sm:text-2xl font-bold mb-4 uppercase tracking-widest">Guest List</h4>
                    {loadingRsvps ? (
                        <p>Loading RSVPs...</p>
                    ) : (
                        <div className="overflow-x-auto">
                            {rsvps.length === 0 ? (
                                <p className="text-center">No RSVPs yet.</p>
                            ) : (
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead>
                                        <tr>
                                            <th className="px-2 py-2 text-left text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                            <th className="px-2 py-2 text-left text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider"># Guests</th>
                                            <th className="px-2 py-2 text-left text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {rsvps.map((rsvp, index) => (
                                            <tr key={index}>
                                                <td className="px-2 py-2 whitespace-nowrap text-sm">{rsvp.name}</td>
                                                <td className="px-2 py-2 whitespace-nowrap text-sm">{rsvp.number_of_guests}</td>
                                                <td className="px-2 py-2 whitespace-nowrap text-sm">{rsvp.attending}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            <button
                                onClick={(e) => { e.stopPropagation(); setShowRsvpForm(true); }}
                                className="mt-6 bg-purple-400 text-white font-bold py-2 px-4 sm:py-3 sm:px-6 rounded-full hover:bg-purple-500 transition-colors w-full"
                            >
                                RSVP
                            </button>
                        </div>
                    )}
                </div>
            )}
          </div>
        </div>
      ) : (
        // Display list of all events
        <div className="w-full max-w-2xl px-2 sm:px-0">
          {events.length === 0 ? (
            <p className="text-center text-lg">No upcoming events.</p>
          ) : (
            events.map((event) => (
              <div 
                key={event.event_id}
                onClick={() => handleEventClick(event)} 
                className="bg-black border border-white rounded-lg p-4 sm:p-6 mb-4 cursor-pointer text-center transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_15px_rgba(255,255,255,0.3)]"
              >
                <h3 className="text-2xl sm:text-3xl font-bold mb-2 uppercase tracking-widest">{event.event_name}</h3>
                <p className="text-md sm:text-lg mb-1">{formatEventDateTime(event.event_date)}, <span className="font-bold">{event.location}</span></p>
                <p className="text-sm sm:text-md">{event.event_snapshot}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Events; 